import {Children,useEffect,useId,useLayoutEffect,useRef,useState} from 'react';
import {installStudioDragScroll} from './studio-scroll.js';
import './studio-workspace.css';

const CLOSED=78;
const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));

export default function StudioWorkspace({children}) {
  const [stage,tools]=Children.toArray(children);
  const root=useRef(null),tray=useRef(null),drag=useRef(null),lastExpanded=useRef(null),ignoreClick=useRef(false);
  const [height,setHeight]=useState(null),[layout,setLayout]=useState({available:0,chrome:140,canvas:160});
  const {available,chrome,canvas}=layout;
  const panelId=useId();
  const maxHeight=Math.max(CLOSED,available-chrome-canvas),minExpanded=Math.min(205,maxHeight);
  const collapsed=height!==null&&height<=CLOSED;
  const panelHeight=drag.current?.moved?clamp(height,CLOSED,maxHeight):collapsed?CLOSED:clamp(height??available*.48,minExpanded,maxHeight);
  useLayoutEffect(()=>{
    const workspace=root.current,area=workspace.querySelector('.creation-area'),stage=area.querySelector('.creation-stage');
    const px=value=>Number.parseFloat(value)||0;
    const measure=()=>{
      const workspaceStyle=getComputedStyle(workspace),areaStyle=getComputedStyle(area);
      const rows=areaStyle.gridTemplateRows.split(' ').map(px),stageRow=px(getComputedStyle(stage).gridRowStart)-1;
      const toolbar=area.querySelector('.focus-toolbar'),toolbarRow=px(getComputedStyle(toolbar).gridRowStart)-1;
      rows[toolbarRow]=Math.max(rows[toolbarRow]||0,toolbar.getBoundingClientRect().height);
      // Reserve controls separately: a wrapping single-nail toolbar must never consume the canvas row.
      const chrome=Math.ceil(rows.reduce((sum,row,index)=>sum+(index===stageRow?0:row),0)+px(areaStyle.rowGap)*(rows.length-1)+px(areaStyle.paddingTop)+px(areaStyle.paddingBottom));
      const available=Math.floor(workspace.clientHeight-px(workspaceStyle.paddingTop)-px(workspaceStyle.paddingBottom)-px(workspaceStyle.rowGap));
      const canvas=area.classList.contains('view-single')?160:120;
      setLayout(previous=>previous.available===available&&previous.chrome===chrome&&previous.canvas===canvas?previous:{available,chrome,canvas});
    };
    // Only intrinsic controls and the workspace affect this calculation. Ignore canvas/tray resizing
    // so moving the handle cannot create an observer feedback loop.
    const observer=new ResizeObserver(measure);
    observer.observe(workspace);
    for(const child of area.children)if(child!==stage)observer.observe(child);
    const modeObserver=new MutationObserver(measure);
    modeObserver.observe(area,{attributes:true,attributeFilter:['class']});
    measure();
    return()=>{observer.disconnect();modeObserver.disconnect();};
  },[]);
  useEffect(()=>installStudioDragScroll(tray.current),[]);
  const expand=()=>setHeight(clamp(lastExpanded.current??available*.48,minExpanded,maxHeight));
  const toggle=()=>{
    if(collapsed)expand();
    else {lastExpanded.current=panelHeight;setHeight(CLOSED);}
  };
  const finish=e=>{
    if(!drag.current||drag.current.id!==e.pointerId)return;
    const current=drag.current;drag.current=null;
    if(current.moved){
      ignoreClick.current=true;
      if(current.height<(CLOSED+minExpanded)/2)setHeight(CLOSED);
      else {const next=clamp(current.height,minExpanded,maxHeight);lastExpanded.current=next;setHeight(next);}
    }else if(e.type==='pointerup'&&e.pointerType!=='mouse'){
      // Some mobile browsers omit/delay click after pointer capture; respond on release.
      ignoreClick.current=true;toggle();
    }
  };
  return <div ref={root} className="workspace-v2 adjustable-workspace" style={{'--studio-area-min-height':`${chrome+canvas}px`,'--studio-canvas-min-height':`${canvas}px`,...(available?{'--studio-tray-height':`${panelHeight}px`}:{})}}>
    {stage}
    <section ref={tray} className={`studio-tool-tray${collapsed?' is-collapsed':''}`} onClickCapture={e=>{
      if(collapsed&&e.target.closest('.st-tabs button,.st-advanced-icon'))expand();
    }}>
      <button type="button" className="studio-tray-handle" aria-label={collapsed?'展开工具面板':'收起工具面板'} aria-expanded={!collapsed} aria-controls={panelId}
        title="上下拖动调整面板，点击收起或展开"
        onClick={e=>{const suppress=ignoreClick.current&&e.detail!==0;ignoreClick.current=false;if(!suppress)toggle();}}
        onPointerDown={e=>{
          if(!e.isPrimary||e.button!==0)return;
          ignoreClick.current=false;drag.current={id:e.pointerId,y:e.clientY,height:panelHeight,start:panelHeight,moved:false};
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={e=>{
          const current=drag.current;if(!current||current.id!==e.pointerId)return;
          const dy=e.clientY-current.y;if(!current.moved&&Math.abs(dy)<5)return;
          current.moved=true;current.height=clamp(current.start-dy,CLOSED,maxHeight);
          setHeight(current.height);
        }} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish}
        onKeyDown={e=>{
          if(!['ArrowUp','ArrowDown','Home','End'].includes(e.key))return;
          e.preventDefault();
          if(e.key==='Home'){if(!collapsed)lastExpanded.current=panelHeight;setHeight(CLOSED);}
          else if(e.key==='End')setHeight(maxHeight);
          else if(e.key==='ArrowUp')setHeight(clamp(panelHeight+48,minExpanded,maxHeight));
          else {const next=panelHeight-48;if(next<minExpanded){lastExpanded.current=panelHeight;setHeight(CLOSED);}else setHeight(next);}
        }}><span aria-hidden="true"/><i aria-hidden="true">{collapsed?'⌃':'⌄'}</i></button>
      <div id={panelId} className="studio-tray-body">{tools}</div>
    </section>
  </div>;
}
